import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Bookmark,
  CheckCircle2,
  CircleAlert,
  Clock3,
  LibraryBig,
  Play,
} from "lucide-react";
import { EmptyState, LoadingLine, PageHeader } from "@/components/primitives";
import { apiFetch } from "@/lib/api";
import { formatPoliticsAccuracy, type PoliticsDashboard } from "@/lib/politics";

export function PoliticsDashboardPage() {
  const query = useQuery({
    queryKey: ["politics", "dashboard"],
    queryFn: () => apiFetch<{ dashboard: PoliticsDashboard }>("/politics/dashboard"),
  });
  const dashboard = query.data?.dashboard;
  const completion = dashboard?.totalQuestions
    ? Math.round((dashboard.answeredQuestions / dashboard.totalQuestions) * 100)
    : 0;

  return (
    <div className="page politics-dashboard">
      <PageHeader title="政治学习概览" description="查看今日答题、正确率、题库进度、错题与收藏。" />
      {query.isLoading ? <LoadingLine /> : null}
      {query.isError ? (
        <EmptyState title="暂时无法读取政治学习数据" description="请稍后重试；若问题持续，请联系管理员检查题库状态。" />
      ) : null}
      {dashboard ? (
        <>
          <section className="politics-dashboard-hero" aria-labelledby="politics-today-title">
            <div className="politics-dashboard-hero__copy">
              <p className="section-label">今日练习</p>
              <h2 id="politics-today-title">
                {dashboard.todayAnswered > 0 ? `今天已完成 ${dashboard.todayAnswered} 题` : "今天，从一道题开始"}
              </h2>
              <p>
                {dashboard.todayAnswered > 0
                  ? `答对 ${dashboard.todayCorrect} 题，当前正确率 ${formatPoliticsAccuracy(dashboard.todayAccuracy)}。`
                  : "选择课程与章节，按自己的节奏完成一组练习。"}
              </p>
              {dashboard.activeSession ? (
                <Link
                  className="button button--primary button--lg"
                  to="/app/$subject/practice/$sessionId"
                  params={{ subject: "politics", sessionId: dashboard.activeSession.id }}
                >
                  <Play /> 继续上次练习
                </Link>
              ) : (
                <Link className="button button--primary button--lg" to="/app/$subject/question-bank" params={{ subject: "politics" }}>
                  <LibraryBig /> 选择练习范围
                </Link>
              )}
            </div>
            <div className="politics-dashboard-hero__metrics" aria-label="今日答题数据">
              <div><strong>{dashboard.todayAnswered}</strong><span>今日答题</span></div>
              <div><strong>{dashboard.todayCorrect}</strong><span>今日正确</span></div>
              <div><strong>{formatPoliticsAccuracy(dashboard.todayAccuracy)}</strong><span>今日正确率</span></div>
            </div>
          </section>

          <section className="politics-dashboard-overview" aria-labelledby="politics-progress-title">
            <header className="politics-section-heading">
              <div><p className="section-label">总体进度</p><h2 id="politics-progress-title">已覆盖 {dashboard.answeredQuestions} / {dashboard.totalQuestions} 题</h2></div>
              <strong>{completion}%</strong>
            </header>
            <div className="politics-progress-track" aria-label={`总体完成 ${completion}%`}>
              <span style={{ width: `${completion}%` }} />
            </div>
            <div className="politics-dashboard-shortcuts">
              <Link to="/app/$subject/question-bank" params={{ subject: "politics" }}>
                <LibraryBig /><span><strong>政治题库</strong><small>按课程与章节练习</small></span><ArrowRight />
              </Link>
              <Link to="/app/$subject/wrong-questions" params={{ subject: "politics" }}>
                <CircleAlert /><span><strong>错题本</strong><small>连续答对两次后移出</small></span><b>{dashboard.wrongCount}</b>
              </Link>
              <Link to="/app/$subject/question-favorites" params={{ subject: "politics" }}>
                <Bookmark /><span><strong>收藏题</strong><small>集中回看重点题目</small></span><b>{dashboard.favoriteCount}</b>
              </Link>
              <Link to="/app/$subject/history" params={{ subject: "politics" }}>
                <Clock3 /><span><strong>答题记录</strong><small>查看历史练习结果</small></span><ArrowRight />
              </Link>
            </div>
          </section>

          <section className="politics-course-progress" aria-labelledby="politics-course-title">
            <header className="politics-section-heading">
              <div><p className="section-label">课程进度</p><h2 id="politics-course-title">五门课程</h2></div>
              <CheckCircle2 />
            </header>
            <div className="politics-course-progress__list">
              {dashboard.courseProgress.map((course) => {
                const percentage = course.totalQuestions
                  ? Math.round((course.answeredQuestions / course.totalQuestions) * 100)
                  : 0;
                return (
                  <article key={course.id}>
                    <div>
                      <strong>{course.name}</strong>
                      <span>{course.answeredQuestions} / {course.totalQuestions} 题 · 正确率 {formatPoliticsAccuracy(course.accuracy)}</span>
                    </div>
                    <div className="politics-progress-track"><span style={{ width: `${percentage}%` }} /></div>
                    <b>{percentage}%</b>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
